/**
 * Crawler Status Card Component
 *
 * Displays Reddit crawler status including health, activity metrics, and failure warnings.
 */

import React from 'react';
import { Paper, Title, Group, Stack, Text, Badge, Loader, Alert } from '@mantine/core';
import { IconBrandReddit, IconAlertTriangle } from '@tabler/icons-react';
import type { CrawlerStatusSection } from '../../../../domain/entities/AdminDashboard';

interface CrawlerStatusCardProps {
  status: CrawlerStatusSection;
  isLoading?: boolean;
}

/**
 * Maps crawler status to appropriate badge color
 */
function getStatusColor(statusValue: string): string {
  switch (statusValue) {
    case 'Running':
      return 'blue';
    case 'Idle':
      return 'green';
    case 'Warning':
      return 'yellow';
    case 'Stopped':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Formats date string to relative time (e.g., "5 minutes ago")
 */
function formatRelativeTime(dateString: string | null): string {
  if (!dateString) {
    return 'Never';
  }

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  } else if (diffMins < 1440) {
    const hours = Math.floor(diffMins / 60);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  } else {
    const days = Math.floor(diffMins / 1440);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
}

export const CrawlerStatusCard: React.FC<CrawlerStatusCardProps> = ({
  status,
  isLoading = false
}): React.ReactElement => {
  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Stack gap="md">
        <Group gap="xs">
          <IconBrandReddit size={24} />
          <Title order={3}>Crawler Status</Title>
        </Group>

        {isLoading ? (
          <Loader size="sm" />
        ) : (
          <>
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                Status:
              </Text>
              <Badge color={getStatusColor(status.status)} variant="light">
                {status.status}
              </Badge>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Last Crawl:
              </Text>
              <Text size="sm" fw={500}>
                {formatRelativeTime(status.lastCrawlTime)}
              </Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Active Sources:
              </Text>
              <Text size="sm" fw={500}>
                {status.activeSourcesCount}
              </Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Mentions Found (24h):
              </Text>
              <Text size="sm" fw={500} c="teal">
                {status.mentionsFoundLast24Hours}
              </Text>
            </Group>

            {status.recentFailuresCount > 0 && (
              <Alert icon={<IconAlertTriangle size={16} />} title="Recent Failures" color="red" variant="light">
                <Group justify="space-between">
                  <Text size="sm">Failures detected:</Text>
                  <Badge color="red" variant="filled" size="sm">
                    {status.recentFailuresCount}
                  </Badge>
                </Group>
              </Alert>
            )}

            {status.message && (
              <Alert color="blue" variant="light">
                <Text size="sm">{status.message}</Text>
              </Alert>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};
